import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useThresholds } from "@/context/ThresholdContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DatabaseBackup, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const { thresholds, setThresholds } = useThresholds();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [nu, setNu] = useState({ email: "", name: "", password: "", role: "operator" });
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    setRows(thresholds.map((t) => ({ ...t })));
  }, [thresholds]);

  const loadUsers = useCallback(() => {
    if (isAdmin) {
      api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
      api.get("/auth/login-logs").then((r) => setLogs(r.data)).catch(() => {});
    }
  }, [isAdmin]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const saveThresholds = async () => {
    try {
      const clean = rows.map((r) => ({ min: Number(r.min), label: r.label, color: r.color })).sort((a, b) => b.min - a.min);
      await api.put("/settings/thresholds", clean);
      setThresholds(clean);
      toast.success("Verimlilik eşikleri kaydedildi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reseed = async () => {
    if (!window.confirm("Tüm tanım ve üretim verileri silinip demo veri yeniden yüklenecek. Emin misiniz?")) return;
    try {
      const r = await api.post("/seed/demo");
      toast.success(`Demo veri yüklendi (${r.data.entries} üretim kaydı)`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addUser = async () => {
    if (!nu.email || !nu.password || !nu.name) { toast.error("Tüm alanlar zorunludur"); return; }
    try {
      await api.post("/auth/users", nu);
      toast.success("Kullanıcı eklendi");
      setNu({ email: "", name: "", password: "", role: "operator" });
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`"${email}" kullanıcısı silinsin mi?`)) return;
    try {
      await api.delete(`/auth/users/${encodeURIComponent(email)}`);
      toast.success("Kullanıcı silindi");
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Ayarlar</h1>
        <p className="text-sm text-muted-foreground mt-1">Verimlilik eşikleri, kullanıcılar ve veri yönetimi</p>
      </div>

      <ThresholdsCard rows={rows} setRows={setRows} isAdmin={isAdmin} onSave={saveThresholds} />

      {isAdmin && <UsersCard users={users} nu={nu} setNu={setNu} addUser={addUser} onDelete={deleteUser} />}

      {isAdmin && <LoginLogsCard logs={logs} />}

      {isAdmin && <DataCard onReseed={reseed} />}
    </div>
  );
}

function ThresholdsCard({ rows, setRows, isAdmin, onSave }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid="thresholds-card">
      <h3 className="text-lg font-medium mb-1">Verimlilik Eşikleri</h3>
      <p className="text-xs text-muted-foreground mb-4">Tüm dashboard ve rozet renkleri bu eşiklere göre belirlenir.</p>
      <div className="space-y-2 max-w-xl">
        {rows.map((r, i) => (
          <div key={r.label} className="flex items-center gap-3">
            <input type="color" value={r.color} data-testid={`threshold-color-${i}`}
              onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, color: e.target.value } : x))}
              className="w-8 h-8 rounded border border-border bg-transparent cursor-pointer" />
            <Input className="w-28" value={r.label} data-testid={`threshold-label-${i}`}
              onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
            <span className="text-xs text-muted-foreground">≥ %</span>
            <Input type="number" className="w-20" value={r.min} data-testid={`threshold-min-${i}`}
              onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, min: e.target.value } : x))} />
          </div>
        ))}
      </div>
      {isAdmin && (
        <Button className="mt-4" onClick={onSave} data-testid="thresholds-save-button">Eşikleri Kaydet</Button>
      )}
    </Card>
  );
}

function DataCard({ onReseed }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid="data-card">
      <h3 className="text-lg font-medium mb-1">Veri Yönetimi</h3>
      <p className="text-xs text-muted-foreground mb-4">Demo veriyi sıfırlayıp yeniden yükler (son 90 gün üretim kaydı dahil).</p>
      <Button variant="outline" onClick={onReseed} data-testid="reseed-button">
        <DatabaseBackup size={15} className="mr-1.5" /> Demo Veriyi Yeniden Yükle
      </Button>
    </Card>
  );
}

function UsersCard({ users, nu, setNu, addUser, onDelete }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid="users-card">
      <h3 className="text-lg font-medium mb-4">Kullanıcılar</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 max-w-3xl">
        <Input placeholder="E-posta" value={nu.email} data-testid="user-email-input"
          onChange={(e) => setNu((u) => ({ ...u, email: e.target.value }))} />
        <Input placeholder="Ad Soyad" value={nu.name} data-testid="user-name-input"
          onChange={(e) => setNu((u) => ({ ...u, name: e.target.value }))} />
        <Input placeholder="Şifre" type="password" value={nu.password} data-testid="user-password-input"
          onChange={(e) => setNu((u) => ({ ...u, password: e.target.value }))} />
        <Select value={nu.role} onValueChange={(v) => setNu((u) => ({ ...u, role: v }))}>
          <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="operator" data-testid="user-role-operator">Operatör</SelectItem>
            <SelectItem value="admin" data-testid="user-role-admin">Yönetici</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={addUser} data-testid="user-add-button"><Plus size={15} className="mr-1" /> Ekle</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">E-posta</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Ad</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Rol</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.email} className="border-border" data-testid={`user-row-${u.email}`}>
              <TableCell className="text-sm py-2">{u.email}</TableCell>
              <TableCell className="text-sm py-2">{u.name}</TableCell>
              <TableCell className="text-sm py-2">{u.role === "admin" ? "Yönetici" : "Operatör"}</TableCell>
              <TableCell className="py-2 text-right">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                  data-testid={`user-delete-${u.email}`} onClick={() => onDelete(u.email)}>
                  <Trash2 size={14} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function LoginLogsCard({ logs }) {
  return (
    <Card className="p-5 bg-card border-border" data-testid="login-logs-card">
      <h3 className="text-lg font-medium mb-1">Sisteme Giriş Kayıtları (IP)</h3>
      <p className="text-xs text-muted-foreground mb-4">Sisteme giriş yapan kullanıcıların IP adresleri ve giriş zamanları.</p>
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tarih / Saat</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Kullanıcı</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">IP Adresi</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tarayıcı / Cihaz</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((l, i) => (
            <TableRow key={`${l.created_at}-${l.email}`} className="border-border" data-testid={`login-log-row-${i}`}>
              <TableCell className="text-sm py-2 font-mono">{new Date(l.created_at).toLocaleString("tr-TR")}</TableCell>
              <TableCell className="text-sm py-2">{l.name || l.email}</TableCell>
              <TableCell className="text-sm py-2 font-mono" data-testid={`login-log-ip-${i}`}>{l.ip || "-"}</TableCell>
              <TableCell className="text-xs py-2 text-muted-foreground max-w-xs truncate">{l.user_agent || "-"}</TableCell>
            </TableRow>
          ))}
          {logs.length === 0 && (
            <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Henüz giriş kaydı yok</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
